import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Subscription } from 'rxjs';

import { MerchantSiteMerchantTypeViewModel } from '@app/api/marketplace/models';
import { merchantRetailerTypes } from '@shared/interfaces/merchant-types';
import { DialogService } from '@shared/services/dialog.service';
import { exciseLicenseNumberValidator } from '@shared/validators/excise-license-number.validator';
import { trafikLicenseNumberValidator } from '@shared/validators/trafik-license-number.validator';

import { merchantTypeGroupOptions } from './merchant-type-group-options';

@Component({
  selector: 'app-merchant-type-excise-subform',
  templateUrl: './merchant-type-excise-subform.component.html',
  styleUrls: ['./merchant-type-excise-subform.component.scss'],
})
export class MerchantTypeExciseSubformComponent implements OnDestroy {
  @Input() set hasExciseProductsOnSite(value: boolean) {
    this.licenseNumbersRequired = value;
    for (let group of this.form.controls) {
      this.setLicenseNumberValidators(group);
    }
  }
  @Output() selectionChanged = new EventEmitter<
    Array<MerchantSiteMerchantTypeViewModel>
  >();

  subs = new Subscription();
  form = new UntypedFormArray([]);

  licenseNumbersRequired = false;

  originalData: Array<MerchantSiteMerchantTypeViewModel> = [];
  prevMerchantTypesAndLicenseNumbers: Array<{
    m: string;
    l: string;
  }> = [];
  createMode = false;
  initFinished = false;

  merchantTypeOptions: Array<Array<any>> = [];

  constructor(private dialogService: DialogService) {}

  initForm(
    data: Array<MerchantSiteMerchantTypeViewModel>,
    form: UntypedFormArray
  ) {
    this.form = form;
    this.merchantTypeOptions = [];
    this.originalData = data;
    this.prevMerchantTypesAndLicenseNumbers = data.map(
      (type: MerchantSiteMerchantTypeViewModel) => ({
        m: type.merchantType,
        l: type.licenseNumber ?? '',
      })
    );

    this.form.clear({ emitEvent: false });
    data.forEach((item) => {
      const group = new UntypedFormGroup({
        merchantType: new UntypedFormControl(item.merchantType),
        licenseNumber: new UntypedFormControl(item.licenseNumber ?? ''),
        requestId: new UntypedFormControl(item.requestId),
        status: new UntypedFormControl(item.status),
      });
      this.setLicenseNumberValidators(group);
      this.form.push(group, { emitEvent: false });
      this.merchantTypeOptions.push([]);
    });
    data.forEach((_item, index) => {
      this.setSelectableOptions(index);
    });
    this.createMode = !data.length;
    if (!data.length) {
      this.addMerchantTypeRow();
    }
    this.initFinished = true;
  }

  addMerchantTypeRow() {
    this.form.push(
      new UntypedFormGroup({
        merchantType: new UntypedFormControl(null),
        licenseNumber: new UntypedFormControl(
          '',
          this.licenseNumbersRequired ? Validators.required : []
        ),
        requestId: new UntypedFormControl(null),
        status: new UntypedFormControl('IN_PROGRESS'),
      }),
      { emitEvent: false }
    );
    this.merchantTypeOptions.push([]);
    this.prevMerchantTypesAndLicenseNumbers.push({ m: '', l: '' });
    this.setSelectableOptions(this.form.length - 1);
  }

  onRemoveMerchantType(index: number) {
    if (this.form.at(index).value.status === 'ACCEPTED') {
      this.dialogService.confirm(
        'DELETE_CONFIRM_TITLE',
        'MERCHANT.DIALOG.DELETE_MERCHANT_TYPE',
        () => {
          this.removeMerchantTypeRow(index);
        }
      );
    } else {
      this.removeMerchantTypeRow(index);
    }
  }

  removeMerchantTypeRow(index: number) {
    this.form.removeAt(index, { emitEvent: false });
    this.merchantTypeOptions.splice(index, 1);
    this.prevMerchantTypesAndLicenseNumbers.splice(index, 1);
    this.setSelectableOptions(index);
    const selectedTypes = this.form.value.filter(
      (type: MerchantSiteMerchantTypeViewModel) => !!type.merchantType
    );
    this.selectionChanged.emit(selectedTypes);
  }

  rowChanged(index: number, merchantTypeChanged = false) {
    const merchantTypeIndexInOriginal = this.originalData.findIndex(
      (item) => item.merchantType === this.form.at(index).value.merchantType
    );
    const licenseNumberIndexInOriginal =
      this.originalData[merchantTypeIndexInOriginal]?.licenseNumber ===
      this.form.at(index).value.licenseNumber
        ? merchantTypeIndexInOriginal
        : -1;

    const isUsualRetailer =
      Array.from(merchantRetailerTypes).includes(
        this.form.at(index).value.merchantType
      ) && this.form.at(index).value.merchantType !== 'RETAILER_AS_TRAFIK';

    if (
      merchantTypeIndexInOriginal > -1 &&
      (merchantTypeIndexInOriginal === licenseNumberIndexInOriginal ||
        isUsualRetailer)
    ) {
      this.form
        .at(index)
        .patchValue(
          { status: this.originalData[merchantTypeIndexInOriginal].status },
          { emitEvent: false }
        );
    } else {
      this.form
        .at(index)
        .patchValue({ status: 'IN_PROGRESS' }, { emitEvent: false });
    }

    if (merchantTypeChanged) {
      this.setLicenseNumberValidators(this.form.at(index));
      this.form.controls.forEach((_item, i) => {
        if (i !== index) {
          this.setSelectableOptions(i);
        }
      });
    }

    if (this.initFinished) {
      const rowValues = this.form.value.filter(
        (type: MerchantSiteMerchantTypeViewModel) => !!type.merchantType
      );
      this.selectionChanged.emit(rowValues);
    }
  }

  setLicenseNumberValidators(group: AbstractControl) {
    const licenseNumberControl = group.get('licenseNumber') as FormControl;
    if (this.licenseNumbersRequired) {
      licenseNumberControl.setValidators(Validators.required);
    }
    const merchantType = group.get('merchantType')?.value;
    switch (merchantType) {
      case 'MANUFACTURER_OR_IMPORTER_AS_TAX_WAREHOUSE':
      case 'MANUFACTURER_OR_IMPORTER_AS_SIMPLIFIED_TAX_WAREHOUSE':
      case 'INTERNATIONAL_TRADER_AS_REGISTERED_DEALER':
      case 'INTERNATIONAL_TRADER_AS_COMMUNITY_EXCISE_WHOLESALER':
      case 'INTERNATIONAL_TRADER_AS_EXPORT_IMPORT_WHOLESALER':
      case 'WHOLESALER_AS_IS':
        licenseNumberControl.setValidators(
          this.licenseNumbersRequired
            ? [Validators.required, exciseLicenseNumberValidator]
            : [exciseLicenseNumberValidator]
        );
        break;
      case 'MANUFACTURER_OR_IMPORTER_AS_SMALL_SCALE_WINE_PRODUCER':
        // no format validation
        licenseNumberControl.setValidators(
          this.licenseNumbersRequired ? [Validators.required] : []
        );
        break;
      case 'RETAILER_AS_HOTEL':
      case 'RETAILER_AS_RESTAURANT':
      case 'RETAILER_AS_CAFE':
      case 'RETAILER_AS_RETAIL_CHAIN':
      case 'RETAILER_AS_OTHER_RETAILER':
        // not required, no format validation
        licenseNumberControl.setValidators([]);
        break;
      case 'RETAILER_AS_TRAFIK':
        // trafik license number is always required
        licenseNumberControl.setValidators([
          Validators.required,
          trafikLicenseNumberValidator,
        ]);
        break;
    }
    licenseNumberControl.updateValueAndValidity({ emitEvent: false });
  }

  getOtherSelectedTypes(index: number) {
    return this.form.value
      .filter(
        (row: any) =>
          row.merchantType !== this.form.at(index).value.merchantType
      )
      .map((row: any) => row.merchantType);
  }

  setSelectableOptions(index: number) {
    const otherSelected = this.form.value
      .filter(
        (row: any) =>
          row.merchantType !== this.form.at(index).value.merchantType
      )
      .map((row: any) => row.merchantType);

    let options = structuredClone(merchantTypeGroupOptions);
    options.map((opt) => {
      if (opt.groupName) {
        opt.options = opt.options.filter(
          (opt) => !otherSelected.includes(opt.value)
        );
      }
      return opt;
    });
    options = options.filter(
      (opt) =>
        !otherSelected.includes(opt.value) &&
        (opt.groupName ? opt.options?.length : true)
    );
    this.merchantTypeOptions[index] = options;
  }

  asGroup(subform: AbstractControl): UntypedFormGroup {
    return subform as UntypedFormGroup;
  }

  get(
    subform: AbstractControl,
    controlName: string
  ): UntypedFormControl | null {
    return this.asGroup(subform)?.get(controlName) as UntypedFormControl;
  }

  removeEmptyRows() {
    for (let i = this.form.controls.length - 1; i >= 0; i--) {
      if (!this.form.at(i).get('merchantType')?.value) {
        this.form.removeAt(i, { emitEvent: false });
      }
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }
}
